import React, { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SectionIntro from "./SectionIntro";

const activities = [
  {
    title: "activities.items.waterfallExploration.title",
    description: "activities.items.waterfallExploration.description",
    media: { type: "video", src: "/images/gallery/waterfall.mp4" },
    aspect: "landscape",
  },
  {
    title: "activities.items.quadBiking.title",
    description: "activities.items.quadBiking.description",
    media: { type: "video", src: "/images/gallery/quad.mp4" },
    aspect: "portrait",
  },
  {
    title: "activities.items.mountainHiking.title",
    description: "activities.items.mountainHiking.description",
    media: { type: "image", src: "/images/gallery/12.jpeg" },
    aspect: "landscape",
  },
];

const Activities = () => {
  const videoRefs = useRef([]);
  const [inViewActivities, setInViewActivities] = useState([]);
  const observerRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    activities.forEach((activity) => {
      if (activity.media.type === "video") {
        const video = document.createElement("video");
        video.src = activity.media.src;
        video.preload = "metadata";
        video.load();
      }
    });

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = parseInt(entry.target.dataset.index);
          const isVideo = activities[index].media.type === "video";
          if (entry.isIntersecting) {
            setInViewActivities((prev) => [...new Set([...prev, index])]);
            if (isVideo) {
              const video = videoRefs.current[index];
              if (video && video.paused) video.play().catch(() => {});
            }
          } else {
            if (isVideo) {
              const video = videoRefs.current[index];
              if (video && !video.paused) video.pause();
            }
            setInViewActivities((prev) => prev.filter((i) => i !== index));
          }
        });
      },
      { threshold: 0.5, rootMargin: "50px 0px 50px 0px" }
    );

    const els = document.querySelectorAll(".activity-item");
    els.forEach((el) => observerRef.current.observe(el));
    return () => observerRef.current && observerRef.current.disconnect();
  }, []);

  const handleVideoPlay = (index) => {
    videoRefs.current.forEach((video, i) => {
      if (video && i !== index && !video.paused) video.pause();
    });
  };

  return (
    <section id="activities" className="py-24 md:py-32 bg-cream-dark/40 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <SectionIntro
          eyebrow={t("labels.experiences")}
          title={t("activities.title")}
          lead={t("activities.subtitle")}
          className="mb-20 md:mb-28"
        />

        <div className="space-y-24 md:space-y-32">
          {activities.map((activity, index) => {
            const reversed = index % 2 === 1;
            return (
              <div
                key={index}
                className={`activity-item flex flex-col gap-10 lg:gap-20 items-center ${
                  reversed ? "lg:flex-row-reverse" : "lg:flex-row"
                }`}
                data-index={index}
              >
                <motion.div
                  className="w-full lg:w-1/2"
                  initial={{ opacity: 0, scale: 1.04 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div
                    className={`relative overflow-hidden bg-cream-dark group ${
                      activity.aspect === "portrait"
                        ? "aspect-[4/5]"
                        : "aspect-[4/3]"
                    }`}
                  >
                    {activity.media.type === "image" ? (
                      <img
                        src={activity.media.src}
                        alt={t(activity.title)}
                        className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <>
                        <video
                          ref={(el) => (videoRefs.current[index] = el)}
                          src={activity.media.src}
                          loop
                          muted
                          playsInline
                          className="w-full h-full object-cover"
                          onPlay={() => handleVideoPlay(index)}
                        />
                        <button
                          aria-label="Play"
                          className={`absolute inset-0 flex items-center justify-center bg-ink/20 transition-opacity duration-500 ${
                            inViewActivities.includes(index)
                              ? "opacity-0 pointer-events-none"
                              : "opacity-100"
                          }`}
                          onClick={() =>
                            videoRefs.current[index]?.play().catch(() => {})
                          }
                        >
                          <span className="w-16 h-16 border border-cream/60 rounded-full flex items-center justify-center text-cream text-xl">
                            ▶
                          </span>
                        </button>
                      </>
                    )}
                  </div>
                </motion.div>

                <motion.div
                  className="w-full lg:w-1/2"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: 0.15 }}
                >
                  <div className={`max-w-lg ${reversed ? "lg:ml-auto" : ""}`}>
                    <span className="font-serif text-5xl md:text-6xl font-light text-brass/40 leading-none">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3 className="mt-5 font-serif font-light text-3xl md:text-4xl text-ink leading-tight">
                      {t(activity.title)}
                    </h3>
                    <div className="rule-gold my-6" />
                    <p className="text-base md:text-lg text-ink-soft leading-relaxed font-light">
                      {t(activity.description)}
                    </p>
                  </div>
                </motion.div>
              </div>
            );
          })}
        </div>

        <motion.div
          className="mt-20 md:mt-24 text-center"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Link
            to="/activity-gallery"
            className="inline-flex items-center gap-3 justify-center bg-ink hover:bg-brass text-cream text-[12px] uppercase tracking-[0.22em] py-4 px-10 transition-colors duration-300"
          >
            {t("activities.viewGallery")}
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </Link>
          <p className="eyebrow text-ink-muted mt-8 text-[10px]">
            {t("activities.footerText")}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Activities;
